import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

/**
 * Comments modal for video posts - slides up from bottom
 */
export function CommentsModal({ post, isOpen, onClose }) {
  const { user, isAuthenticated } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  const loadComments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getComments(post.id);
      setComments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load comments:', error);
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [post?.id]);

  useEffect(() => {
    if (isOpen && post?.id) {
      loadComments();
    }
  }, [isOpen, post?.id, loadComments]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Focus input when modal opens
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim() || !isAuthenticated) return;

    setSubmitting(true);
    try {
      const comment = await apiService.createComment(
        post.id,
        newComment.trim(),
        replyTo?.id
      );

      // Add new comment to list
      if (replyTo) {
        // If it's a reply, refresh the list to show nested structure
        await loadComments();
      } else {
        // Add to top for top-level comments
        setComments([comment, ...comments]);
      }

      setNewComment('');
      setReplyTo(null);
    } catch (error) {
      console.error('Failed to post comment:', error);
      alert('Failed to post comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async (commentId) => {
    if (!isAuthenticated) return;

    try {
      const comment = comments.find(c => c.id === commentId);
      if (!comment) return;

      if (comment.is_liked) {
        await apiService.unlikeComment(commentId);
      } else {
        await apiService.likeComment(commentId);
      }

      // Update local state
      setComments(comments.map(c => 
        c.id === commentId 
          ? { 
              ...c, 
              is_liked: !c.is_liked,
              like_count: c.like_count + (c.is_liked ? -1 : 1)
            }
          : c
      ));
    } catch (error) {
      console.error('Failed to like comment:', error);
    }
  };

  const handleReply = (comment) => {
    setReplyTo(comment);
    inputRef.current?.focus();
  };

  const cancelReply = () => {
    setReplyTo(null);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-gray-900 rounded-t-3xl max-h-[80vh] flex flex-col animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-white font-semibold text-lg">
            {comments.length} {comments.length === 1 ? 'Comment' : 'Comments'}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-white transition"
          >
            ✕
          </button>
        </div>

        {/* Comments list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent" />
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <div className="text-4xl mb-2">💬</div>
              <p>No comments yet</p>
              <p className="text-sm">Be the first to comment!</p>
            </div>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="flex gap-3">
                {/* Avatar */}
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-400 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                  {comment.username?.[0]?.toUpperCase() || comment.name?.[0]?.toUpperCase() || 'U'}
                </div>

                {/* Comment content */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white font-semibold text-sm">
                      {comment.username || comment.name || 'Anonymous'}
                    </span>
                    {comment.is_verified && (
                      <span className="text-blue-400 text-xs">✓</span>
                    )}
                    <span className="text-gray-500 text-xs">
                      {new Date(comment.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  
                  <p className="text-gray-200 text-sm mb-2">{comment.content}</p>

                  {/* Comment actions */}
                  <div className="flex items-center gap-4 text-xs text-gray-400">
                    <button
                      onClick={() => handleLike(comment.id)}
                      className={`flex items-center gap-1 hover:text-red-400 transition ${
                        comment.is_liked ? 'text-red-400' : ''
                      }`}
                      disabled={!isAuthenticated}
                    >
                      {comment.is_liked ? '❤️' : '🤍'} 
                      <span>{comment.like_count || 0}</span>
                    </button>
                    
                    {isAuthenticated && !comment.parent_comment_id && (
                      <button
                        onClick={() => handleReply(comment)}
                        className="hover:text-purple-400 transition"
                      >
                        Reply
                      </button>
                    )}
                  </div>

                  {/* Show replies if they exist */}
                  {comment.replies && comment.replies.length > 0 && (
                    <div className="mt-3 space-y-3 pl-4 border-l-2 border-gray-700">
                      {comment.replies.map(reply => (
                        <div key={reply.id} className="flex gap-2">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-green-400 to-blue-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {reply.username?.[0]?.toUpperCase() || 'U'}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-white font-semibold text-xs">
                                {reply.username || 'Anonymous'}
                              </span>
                              <span className="text-gray-500 text-xs">
                                {new Date(reply.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-gray-200 text-sm">{reply.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Comment input */}
        {isAuthenticated ? (
          <form onSubmit={handleSubmit} className="p-4 border-t border-gray-700">
            {replyTo && (
              <div className="mb-2 flex items-center gap-2 text-sm text-gray-400">
                <span>Replying to @{replyTo.username}</span>
                <button
                  type="button"
                  onClick={cancelReply}
                  className="text-gray-500 hover:text-white"
                >
                  ✕
                </button>
              </div>
            )}
            
            <div className="flex gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-400 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                {user?.username?.[0]?.toUpperCase() || 'U'}
              </div>
              
              <input
                ref={inputRef}
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={replyTo ? `Reply to @${replyTo.username}...` : "Add a comment..."}
                className="flex-1 bg-gray-800 text-white px-4 py-2 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={submitting}
              />
              
              <button
                type="submit"
                disabled={!newComment.trim() || submitting}
                className="px-6 py-2 bg-purple-500 text-white rounded-full font-semibold hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {submitting ? '...' : 'Post'}
              </button>
            </div>
          </form>
        ) : (
          <div className="p-4 border-t border-gray-700 text-center text-gray-400">
            <p className="mb-2">Sign in to comment</p>
            <button
              onClick={() => window.location.href = '/login'}
              className="px-6 py-2 bg-purple-500 text-white rounded-full font-semibold hover:bg-purple-600 transition"
            >
              Sign In
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
        
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
}

export default CommentsModal;
